/**
 * Marketplace-aware API helpers for Fulfillment panels and TSD (WB vs Ozon).
 * Ozon list endpoints use 1-based page; WB / TablePagination — 0-based.
 * Warehouse calls go through `/fulfillment/unified` (legacy WB/Ozon paths kept).
 */

/** Normalize query/UI marketplace to `wb` | `ozon` | `yandex` | ''. */
export function normalizeFulfillmentMp(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'ozon' || raw === '2') return 'ozon';
  if (raw === 'wb' || raw === '1' || raw === 'wildberries') return 'wb';
  if (raw === 'yandex' || raw === '3' || raw === 'ym') return 'yandex';
  return '';
}

/**
 * Единая warehouse-база (picking / labeling / pallets / loading).
 * Маркетплейс определяется на бэкенде по account_id (и опционально marketplace).
 */
export function fulfillmentApiBase(_marketplace) {
  return '/fulfillment/unified';
}

export function isLocalWarehouseMp(marketplace) {
  const mp = normalizeFulfillmentMp(marketplace);
  return mp === 'ozon' || mp === 'yandex';
}

export function fulfillmentListPage(marketplace, zeroBasedPage) {
  const page = Math.max(0, Number(zeroBasedPage) || 0);
  return isLocalWarehouseMp(marketplace) ? page + 1 : page;
}

/** Для unified warehouse facade account_id (+ marketplace) нужны и WB, и Ozon. */
export function withAccountBody(marketplace, accountId, body = {}) {
  const next = { ...body };
  if (accountId != null && accountId !== '') next.account_id = accountId;
  const mp = normalizeFulfillmentMp(marketplace);
  if (mp) next.marketplace = mp;
  return next;
}

export function withAccountParams(marketplace, accountId, params = {}) {
  const next = { ...params };
  if (accountId != null && accountId !== '') next.account_id = accountId;
  const mp = normalizeFulfillmentMp(marketplace);
  if (mp) next.marketplace = mp;
  return next;
}

/** Marketplace of a unified/WB/Ozon account row. */
export function accountMarketplace(account) {
  const fromField = normalizeFulfillmentMp(account?.marketplace);
  if (fromField) return fromField;
  // Legacy WB rows without marketplace
  return 'wb';
}

/**
 * Pick cabinet id from unified accounts list using deep-link query.
 * Priority: exact account_id → first cabinet of mp → first account.
 */
export function pickFulfillmentAccount(accounts, query = {}) {
  const list = Array.isArray(accounts) ? accounts : [];
  if (!list.length) return '';

  const wantedId = query.account_id ?? query.accountId;
  if (wantedId != null && String(wantedId).trim() !== '') {
    const byId = list.find((item) => String(item.id) === String(wantedId));
    if (byId) return String(byId.id);
  }

  const mp = normalizeFulfillmentMp(query.mp ?? query.marketplace);
  if (mp) {
    const byMp = list.find((item) => {
      const itemMp = normalizeFulfillmentMp(item.marketplace);
      if (itemMp === mp) return true;
      // Legacy WB rows without marketplace field
      return mp === 'wb' && !itemMp;
    });
    if (byMp) return String(byMp.id);
  }

  return String(list[0].id);
}

/** Build `/fulfillment?...` path for redirects and in-app links. */
export function fulfillmentDeepLinkPath({ mp, accountId } = {}) {
  const params = new URLSearchParams();
  const norm = normalizeFulfillmentMp(mp);
  if (norm) params.set('mp', norm);
  if (accountId != null && String(accountId).trim() !== '') {
    params.set('account_id', String(accountId));
  }
  const q = params.toString();
  return q ? `/fulfillment?${q}` : '/fulfillment';
}

/**
 * Deep-link for TSD pages: `/fulfillment/labeling?mp=ozon&account_id=7`.
 * `basePath` without query (e.g. `/fulfillment/picking` or `/fulfillment/picking/12`).
 */
export function fulfillmentTsdPath(basePath, { mp, accountId } = {}) {
  const path = String(basePath || '/fulfillment').split('?')[0] || '/fulfillment';
  const params = new URLSearchParams();
  const norm = normalizeFulfillmentMp(mp);
  if (norm) params.set('mp', norm);
  if (accountId != null && String(accountId).trim() !== '') {
    params.set('account_id', String(accountId));
  }
  const q = params.toString();
  return q ? `${path}?${q}` : path;
}

/** Map Ozon labeling next_step → WB-compatible UI status. */
export function mapOzonLabelingStep(nextStep) {
  const step = String(nextStep || '').trim();
  if (step === 'confirm_applied') return 'awaiting_confirm';
  if (step === 'verify_attach') return 'awaiting_verify';
  if (step === 'print' || step === 'print_pending') return 'print_pending';
  return 'awaiting_label';
}

/** Present Ozon posting as a task-like object for shared TSD UI. */
export function ozonPostingAsTask(posting = {}) {
  const number = posting.posting_number != null ? String(posting.posting_number) : null;
  return {
    ...posting,
    marketplace: posting.marketplace || 'ozon',
    account_id: posting.account_id,
    posting_number: number,
    wb_order_id: number,
    article: posting.offer_id || posting.article || posting.product?.article || null,
    nm_id: posting.sku ?? posting.nm_id ?? null,
    barcode: posting.barcode || posting.product?.barcode || null,
    image_url: posting.image_url || posting.product?.image_url || null,
    warehouse_id: posting.warehouse_id ?? null,
    delivery_method_id: posting.delivery_method_id ?? null,
    cargo_type: posting.cargo_type ?? null,
  };
}

/** Present Yandex order as a task-like object for shared TSD UI. */
export function yandexOrderAsTask(order = {}) {
  const number = order.yandex_order_id != null
    ? String(order.yandex_order_id)
    : (order.posting_number != null ? String(order.posting_number) : null);
  return {
    ...order,
    marketplace: order.marketplace || 'yandex',
    account_id: order.account_id,
    yandex_order_id: number,
    posting_number: number,
    wb_order_id: number,
    article: order.offer_id || order.article || order.product?.article || null,
    nm_id: order.sku ?? order.nm_id ?? null,
    barcode: order.barcode || order.product?.barcode || null,
    image_url: order.image_url || order.product?.image_url || null,
    warehouse_id: order.warehouse_id ?? null,
    cargo_type: order.cargo_type ?? null,
  };
}

function marketplaceOrderAsTask(row, marketplace) {
  return normalizeFulfillmentMp(marketplace) === 'yandex'
    ? yandexOrderAsTask(row)
    : ozonPostingAsTask(row);
}

/**
 * Normalize labeling queue row for shared UI.
 * WB: `{ task, status, scan_hint, ... }`; Ozon: flat posting (+ scan_hint).
 */
export function normalizeLabelingQueueRow(row, marketplace) {
  if (!row) return null;
  if (!isLocalWarehouseMp(marketplace)) return row;

  const posting = row.posting_number != null || row.yandex_order_id != null
    || row.offer_id != null || row.sku != null
    ? row
    : (row.posting || row.task || {});
  const task = marketplaceOrderAsTask(posting, marketplace);
  let status = row.status || null;
  if (!status) {
    if (posting.label_applied_at) status = 'awaiting_verify';
    else if (posting.label_printed_at) status = 'awaiting_confirm';
    else status = 'awaiting_label';
  }
  return {
    ...row,
    task,
    status,
    scan_hint: row.scan_hint
      || (status === 'awaiting_verify'
        ? (normalizeFulfillmentMp(marketplace) === 'yandex'
          ? 'Сканируй этикетку грузоместа'
          : 'Сканируй этикетку заказа')
        : 'Сканируй товар'),
  };
}

/**
 * Ozon: товарный ШК, по которому нашли отправление, не должен попадать в сверку
 * этикетки — иначе «На палету» уходит с кодом товара.
 */
export function ozonVerifyPrefillCode(task, scannedCode = '') {
  const code = String(scannedCode || '').trim();
  if (!code) return '';
  const productCodes = [task?.barcode, task?.offer_id, task?.article]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);
  return productCodes.includes(code) ? '' : code;
}

/** Prefill verify field only with a cargo-place-like scan (not product EAN). */
export function yandexVerifyPrefillCode(task, scannedCode = '') {
  const code = String(scannedCode || '').trim();
  const orderId = String(task?.yandex_order_id || task?.posting_number || '').trim();
  if (!code || !orderId) return '';
  if (code === orderId) return code;
  if (code.startsWith(`${orderId}-`) || code.startsWith(`${orderId}#`)) return code;
  return '';
}

/** Normalize labeling scan/resolve payload for shared UI. */
export function normalizeLabelingResolve(data, marketplace, scannedCode = '') {
  if (!isLocalWarehouseMp(marketplace)) return data;

  const posting = data?.posting || data?.order || data?.selected?.task || data?.task || {};
  const task = marketplaceOrderAsTask(posting, marketplace);
  const status = mapOzonLabelingStep(data?.next_step || data?.status);
  const scanned = String(scannedCode || '').trim();
  const verifyPrefill = normalizeFulfillmentMp(marketplace) === 'yandex'
    ? yandexVerifyPrefillCode(task, scanned)
    : ozonVerifyPrefillCode(task, scanned);
  return {
    ...data,
    selected: { task, job: data?.job || null, item: data?.item || null },
    status,
    scan_hint: data?.scan_hint,
    scanned_code: scanned,
    verify_prefill: status === 'awaiting_verify' ? verifyPrefill : '',
  };
}

/**
 * Body fields that identify an order for labeling APIs.
 * WB → wb_order_id; Ozon → posting_number.
 */
export function labelingOrderBody(marketplace, task, extra = {}) {
  const accountId = task?.account_id;
  const mp = normalizeFulfillmentMp(marketplace);
  if (mp === 'ozon') {
    return {
      account_id: accountId,
      posting_number: task?.posting_number || task?.wb_order_id,
      ...extra,
    };
  }
  if (mp === 'yandex') {
    const orderId = task?.yandex_order_id || task?.posting_number || task?.wb_order_id;
    return {
      account_id: accountId,
      yandex_order_id: orderId,
      posting_number: orderId,
      ...extra,
    };
  }
  return {
    account_id: accountId,
    wb_order_id: task?.wb_order_id,
    ...extra,
  };
}

/**
 * Совместимость склада/DM для активной палеты ТСД.
 * Пустое у заказа = совпадает с заполненным у палеты (фолбэк единственного склада кабинета).
 * Два разных непустых id — разные палеты.
 */
export function labelingScopeCompatible(palletValue, orderValue) {
  const norm = (value) => {
    if (value == null || value === '') return null;
    const text = String(value).trim();
    if (!text || text === 'null' || text === 'undefined') return null;
    return text;
  };
  const palletNorm = norm(palletValue);
  const orderNorm = norm(orderValue);
  if (palletNorm === orderNorm) return true;
  if (palletNorm == null || orderNorm == null) return true;
  return false;
}

/** Create-pallet body from a queue/task seed. */
export function labelingCreatePalletBody(marketplace, accountId, task = {}) {
  const mp = normalizeFulfillmentMp(marketplace);
  if (mp === 'ozon') {
    return withAccountBody(marketplace, accountId, {
      delivery_method_id: task.delivery_method_id,
      warehouse_id: task.warehouse_id,
    });
  }
  if (mp === 'yandex') {
    return withAccountBody(marketplace, accountId, {
      warehouse_id: task.warehouse_id,
    });
  }
  return {
    account_id: task.account_id || accountId,
    from_wb_order_id: task.wb_order_id,
  };
}

/** Verify-attach body: WB sticker_code vs Ozon scanned_barcode. */
export function labelingVerifyBody(marketplace, task, { code, palletId } = {}) {
  const base = labelingOrderBody(marketplace, task, { pallet_id: palletId });
  if (isLocalWarehouseMp(marketplace)) {
    return { ...base, scanned_barcode: code, code };
  }
  return { ...base, sticker_code: code };
}

/** Confirm button label for labeling step (Ozon has no WB technical supply). */
export function labelingConfirmLabel(status, marketplace, task = null) {
  if (status === 'awaiting_label') {
    return normalizeFulfillmentMp(marketplace) === 'wb'
      ? 'В поставку и печать'
      : 'Печать';
  }
  if (status === 'print_pending') return 'Печать';
  if (status === 'awaiting_confirm') return 'Этикетка наклеена';
  if (status === 'awaiting_verify') {
    if (normalizeFulfillmentMp(marketplace) === 'yandex') {
      const total = Number(task?.labels_total);
      const verified = Number(task?.labels_verified);
      if (Number.isFinite(total) && total > 1 && verified < total - 1) {
        return 'Подтвердить грузоместо';
      }
    }
    return 'На палету';
  }
  return 'Подтвердить';
}

/** Whether TSD labeling supports cancel→errors (WB only). */
export function labelingSupportsCancel(marketplace) {
  return normalizeFulfillmentMp(marketplace) === 'wb';
}

/** Whether picking uses resolve+confirm (WB) vs single scan (Ozon). */
export function pickingUsesTwoStepConfirm(marketplace) {
  return normalizeFulfillmentMp(marketplace) === 'wb';
}

/**
 * Normalize picking-job item for shared TSD list UI.
 * Ozon items expose `posting`; WB items expose `task`.
 */
export function normalizePickingJobItem(item, marketplace) {
  if (!item) return item;
  if (!isLocalWarehouseMp(marketplace)) return item;
  const posting = item.posting || item.order || {};
  const task = marketplaceOrderAsTask({
    ...posting,
    account_id: item.account_id || posting.account_id,
    yandex_order_id: posting.yandex_order_id || item.yandex_order_id,
    posting_number: posting.posting_number || item.posting_number,
  }, marketplace);
  return {
    ...item,
    task,
    product: {
      article: item.product?.article || task.article,
      image_url: item.product?.image_url || task.image_url,
      barcode: item.product?.barcode || task.barcode,
    },
  };
}

export function yandexShipmentClosePath(shipmentId) {
  return `/fulfillment/yandex/shipments/${encodeURIComponent(String(shipmentId))}/close`;
}

export function yandexShipmentBarcodePrintPath(shipmentId) {
  return `/fulfillment/yandex/shipments/${encodeURIComponent(String(shipmentId))}/barcode-print`;
}

export function yandexShipmentActPath(shipmentId) {
  return `/fulfillment/yandex/shipments/${encodeURIComponent(String(shipmentId))}/act`;
}

/** Whether loading session needs account_id (Ozon) and supports vehicle label (WB). */
export function loadingSessionCapabilities(marketplace) {
  const mp = normalizeFulfillmentMp(marketplace);
  const isOzon = mp === 'ozon';
  const isWb = mp === 'wb';
  return {
    requiresAccount: isOzon || mp === 'yandex',
    supportsVehicleLabel: isWb,
    supportsFinalSuppliesList: isWb,
  };
}
